<template>
  <div class="input-box">
    <div class="bar__box" :class="{ 'is-disabled': disabled }">
      <div class="bar__input-wrap">
        <el-input
          ref="textarea"
          v-model="inputValue"
          type="textarea"
          :rows="1"
          :autosize="{ minRows: 1, maxRows: 6 }"
          :placeholder="placeholder || $t('inputBox.nkywwrhgyptdxx')"
          :disabled="disabled"
          :aria-label="placeholder || $t('inputBox.nkywwrhgyptdxx')"
          @keydown.native="handleKeydown"
          @compositionstart.native="isComposing = true"
          @compositionend.native="isComposing = false"
          @input="$emit('input', inputValue)"
        />
      </div>

      <div class="bar__actions">
        <el-tooltip :content="isStreaming ? $t('inputBox.ting_zhi_sheng_cheng') : $t('inputBox.fa_song')" placement="top">
          <span class="send-wrap">
            <button
              v-if="isStreaming"
              type="button"
              class="send-btn"
              :aria-label="$t('inputBox.ting_zhi_sheng_cheng')"
              @click="$emit('stop')"
            >
              <i class="icon icon-fasongzhong" aria-hidden="true"></i>
            </button>
            <button
              v-else
              type="button"
              class="send-btn"
              :aria-label="$t('inputBox.fa_song')"
              :disabled="!canSend"
              @click="handleSend"
            >
              <i class="icon icon-fasong" aria-hidden="true"></i>
            </button>
          </span>
        </el-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InputBox',
  props: {
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isStreaming: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inputValue: this.value,
      isComposing: false,
    };
  },
  computed: {
    canSend() {
      return !this.disabled && !this.isStreaming && this.inputValue.trim().length > 0;
    },
  },
  watch: {
    value(val) {
      this.inputValue = val;
    },
  },
  methods: {
    handleKeydown(event) {
      // 中文输入法确认候选词时不发送，Shift + Enter 保留换行行为。
      if (event.key !== 'Enter' || event.shiftKey || event.ctrlKey || event.altKey || event.metaKey
        || this.isComposing || event.isComposing || event.keyCode === 229) {
        return;
      }
      event.preventDefault();
      this.handleSend();
    },
    handleSend() {
      if (!this.canSend) {
        return;
      }
      // 是否清空草稿由父组件决定，发送被拒绝时仍保留输入。
      this.$emit('send', this.inputValue.trim());
    },
    focus() {
      if (this.$refs.textarea) {
        this.$refs.textarea.focus();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.input-box {
  width: 100%;
  min-width: 0;
}

// 输入区：设计稿 398×105、圆角 21、底色 #FCFCFD、0.8px 描边、内边距 14/12/6/12
.bar__box {
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
  width: 100%;
  min-height: 105px;
  padding: 14px 12px 6px;
  background: #fcfcfd;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 21px;
  transition: border-color 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  &:focus-within { border-color: rgba(0, 0, 0, 0.16); }

  // 生成中只禁止交互，不整体降透明度：设计稿里输入框内的图标和停止按钮都保持原色
  &.is-disabled {
    background: #fcfcfd;
    cursor: not-allowed;
  }
}

.bar__input-wrap {
  flex: 1;
  min-width: 0;

  ::v-deep .el-textarea__inner {
    min-height: 32px !important;
    padding: 0;
    border: 0;
    border-radius: 0;
    outline: none;
    background: transparent;
    box-shadow: none;
    color: #2c2c2c;
    font-family: inherit;
    // 设计稿占位文字 12px/16 #848691
    font-size: 12px;
    line-height: 16px;
    resize: none;

    &::placeholder {
      color: #848691;
    }

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #d9dde2;
      border-radius: 2px;
    }
  }
}

.bar__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 32px;
  flex-shrink: 0;
}

.send-wrap {
  display: inline-flex;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  // 靠 margin 让发送按钮保持右对齐
  margin-left: auto;
}

.send-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 0;
  border: 0;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;

  &:focus-visible {
    outline: 2px solid #3675d8;
    outline-offset: 3px;
  }

  &:disabled {
    cursor: not-allowed;
  }

  i {
    font-size: 20px;
  }
}

// 设计稿发送/停止就是图标本体（实心圆 + 箭头或方块），不再叠加底色
.send-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: #000311;
  background: transparent;

  &:hover:not(:disabled),
  &:active:not(:disabled) { opacity: 0.75; }

  // 不可发送态：整体降低不透明度
  &:disabled { opacity: 0.3; }

  .icon { font-size: 32px; }

  // 可发送态：设计稿为主题蓝 #2563EB 实心圆 + 白色箭头
  .icon-fasong { color: #2563eb; }
  // 不可发送（没输入内容）时保持设计稿的中性深色，只降不透明度
  &:disabled .icon-fasong { color: #000311; }
  // 停止态：设计稿为深色 #000311 实心圆 + 白色方块
  .icon-fasongzhong { color: #000311; }
}

</style>
